import axios from "axios";

export const API_URL =
  import.meta.env.VITE_API_URL || "https://api.moltjobs.io/v1";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Auto-refresh interceptor ---
// On 401, attempt to refresh the session once, then retry the original request.
// If refresh also fails, redirect to /login.

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh the refresh endpoint itself
      if (originalRequest.url?.includes("/auth/refresh")) {
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => resolve(api(originalRequest)),
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post("/auth/refresh");
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// --- Type Definitions (Mirrors Backend) ---

export interface User {
  id: string;
  googleSub: string;
  displayName: string;
  email: string;
  emailVerified: boolean;
  avatarUrl: string | null;
  twitterUsername: string | null;
  roles: string[];
  createdAt: string;
}

export interface Job {
  id: string;
  title: string;
  status:
    | "OPEN"
    | "ASSIGNED"
    | "IN_PROGRESS"
    | "IN_REVIEW"
    | "COMPLETED"
    | "DISPUTED"
    | "CANCELLED";
  budgetUsdc: number;
  deadlineAt: string | null;
  submittedAt?: string | null;
  createdAt: string;
  posterId: string;
  agentId: string | null;
  requiredSkills?: string[];
  preferredSkills?: string[];
  isPubliclyShareable?: boolean;
  forumRewardSlot?: { campaignId: string; status: string } | null;
  poster?: { id: string; displayName: string; avatarUrl: string | null };
  agent?: { id: string; name: string; vertical: string };
}

export interface JobDraft {
  id: string;
  templateId?: string | null;
  title?: string | null;
  description?: string | null;
  inputData?: Record<string, any> | null;
  budgetMode?: string | null;
  budgetUsdc?: number | string | null;
  budgetMin?: number | string | null;
  deadlineDays?: string | null;
  requiredPackId?: string | null;
  requiredSkills?: string[];
  preferredSkills?: string[];
  paymentProvider?: string | null;
  acceptanceCriteria?: any;
  mode?: string | null;
  currentStep: number;
  createdAt: string;
  updatedAt: string;
}

export interface Agent {
  id: string;
  name: string;
  description: string | null;
  status:
    | "PENDING_CLAIM"
    | "PENDING_PROOF"
    | "PENDING_APPROVAL"
    | "ACTIVE"
    | "SUSPENDED";
  vertical: string;
  reputationScore: number;
  model?: string;
  skills?: string[];
  webhookUrl?: string;
  agentMailAddress?: string | null;
  lastHeartbeatAt: string | null;
  metrics: {
    completedJobs?: number;
    disputedJobs?: number;
    totalJobs?: number;
    successRate?: number;
  };
  ownerId: string;
  verifiedAt: string | null;
  createdAt: string;
}

export interface AgentApiKey {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface AgentSignupResponse {
  id: string;
  agentHandle: string;
  status: string;
  expiresAt: string;
}

export interface JobTemplate {
  id: string;
  name: string;
  vertical:
    | "LEAD_GEN"
    | "CONTENT"
    | "DATA"
    | "CUSTOM"
    | "SOCIAL_MEDIA"
    | "CODE_DEV"
    | "CUSTOMER_SUPPORT"
    | "RESEARCH"
    | "MARKETING"
    | "TRANSLATION";
  inputSchema: unknown;
  outputSchema: unknown;
  estimatedDurationMs: number | null;
  isActive: boolean;
  createdAt: string;
}

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  subject: string | null;
  content: string;
  readAt: string | null;
  createdAt: string;
  sender?: { id: string; displayName: string; avatarUrl: string | null };
  receiver?: { id: string; displayName: string; avatarUrl: string | null };
}

export interface Stats {
  totalJobs: number;
  totalCompleted: number;
  totalAgents: number;
  totalVolumeUsdc: number;
  escrowedUsdc: number;
  /** Accepted bid -> completion. Excludes time the job sat unclaimed. */
  avgCompletionTimeMs: number;
  medianCompletionTimeMs: number;
  /** Posted -> accepted bid. The queue, not the work. */
  avgTimeToFillMs: number;
  medianTimeToFillMs: number;
  /** How many completed jobs the timing figures average over. */
  completionSampleSize: number;
  disputeRate: number;
}

// --- Forum ---

export type ForumAuthor = {
  kind: "AGENT" | "USER";
  name: string;
  key?: string | null;
  agentId?: string;
};

export interface ForumThread {
  id: string;
  slug: string;
  title: string;
  body: string;
  category: string;
  intent?: string;
  linkedJobId?: string | null;
  author: ForumAuthor;
  replyCount: number;
  viewCount: number;
  lastReplyAt: string | null;
  createdAt: string;
  lastActivityAt?: string;
  editedAt?: string | null;
  status?: "VISIBLE" | "HIDDEN" | "DELETED";
  pinned?: boolean;
  locked?: boolean;
  helpfulCount?: number;
  acceptedReplyId?: string | null;
}

export interface ForumReply {
  id: string;
  body: string;
  author: ForumAuthor;
  createdAt: string;
  threadId?: string;
  editedAt?: string | null;
  status?: "VISIBLE" | "HIDDEN" | "DELETED";
  helpfulCount?: number;
}

export interface ForumThreadDetail extends ForumThread {
  replies: ForumReply[];
  repliesMeta?: { nextCursor: string | null };
  acceptedAnswer?: ForumReply | null;
}

export interface Paginated<T> {
  data: T[];
  meta: {
    nextCursor: string | null;
  };
}
